import { Raycaster, Vector2, Vector3 } from 'three';
import { appMixin } from '../utils/appMixin';
import { Camera } from './Camera';
import { Ground } from './Objects/Ground';
import { Kong } from './Objects/Kong';

import { Renderer } from './Renderer';
import { Scene } from './Scene';
import { Halo } from './Objects/Halo';
import { Particles } from './Objects/Particles';
import { Rain } from './Objects/Rain';
import { Background } from './Objects/Background';
import { Text } from './Objects/Text';

import { Arena } from './Objects/Arenas/Arena';
import { Controls } from '../Tools/Controls';
import { PostProcessing } from './PostProcessing';

export class Webgl extends appMixin() {
  constructor(app) {
    super(app);

    this.controlsDisabled = this.app.params.controlsDisabled;
    this.rainDisabled = this.app.params.rainDisabled;
    this.particlesDisabled = this.app.params.particlesDisabled;
    this.transparentBg = this.app.params.transparentBg;
    this.auraEnabled = this.app.params.auraEnabled;
    this.hasArena = this.app.params.arena != null;

    // this.hasWeapon = this.app.params.kong.weapon != null;
    // this.hasShield = this.app.params.kong.shield != null;
    // this.hasArmor = this.app.params.kong.armor != null;
    // this.hasHat = this.app.params.kong.hat != null;

    this.hasEquipments = this.app.params.kong.equipments?.length > 0;

    this.hasText = !!this.app.params.text?.active;

    this.renderer = new Renderer(this.app);
    this.postProcessing = new PostProcessing(this.app);
    this.scene = new Scene(this.app);
    this.camera = new Camera(this.app);

    this.scene.position.y = -1;

    this.raycaster = new Raycaster();
    this.pointer = new Vector2(0, 0);

    this.t = 0;
  }

  onAttach() {
    this.app.$parent.appendChild(this.renderer.domElement);

    this.renderer.setClearAlpha(this.transparentBg ? 0 : 1);

    this.kong = new Kong(this.app);
    this.scene.add(this.kong);

    // if (this.hasWeapon) this.kong.addPart('weapon', 'Right_Hand-Local', new Vector3(-6, 0, 0), new Vector3(-Math.PI, 0, 0));
    // if (this.hasShield) this.kong.addPart('shield', 'Left_Hand-Local', new Vector3(8, 0, 0), new Vector3(Math.PI * 0.5, Math.PI, 0));
    if (this.hasEquipments)
      this.kong.addParts(...this.app.params.kong.equipments.map((_, i) => `equip_${i}`));

    if (!this.transparentBg) {
      this.ground = new Ground(this.app);
      this.halo = new Halo(this.app);
      this.background = new Background(this.app);

      if (!this.rainDisabled) this.rain = new Rain(this.app);
      if (!this.particlesDisabled) this.particles = new Particles(this.app);

      this.scene.add(this.ground, this.halo);

      this.arena = new Arena(this.app);
      this.scene.add(this.arena);
      this.arena.visible = false;

      if (this.hasArena) {
        // this.background.visible = false;
        this.arena.visible = true;

        this.arena.updateModel(this.app.core.loader.getModel('arena'));
      } else {
        if (!this.rainDisabled) this.scene.add(this.rain);
        if (!this.particlesDisabled) this.scene.add(this.particles);
      }
      this.scene.add(this.background);
    }

    if (this.hasText) this.initText();

    this.kong.open();
    if (this.hasArena) this.arena.open();

    this.controls = new Controls(this.app, this.camera, this.kong, this.app.$parent);
  }

  updateText() {
    if (this.hasText) this.text.update(this.app.params.text);
    else this.initText();
  }

  initText() {
    this.text = new Text(this.app, this.app.params.text);
    this.scene.add(this.text);
  }

  onResize({ height }) {
    if (this.renderer?.domElement) this.renderer.domElement.style.height = `${height}px`;
  }

  raycast(pointer) {
    this.raycaster.setFromCamera(pointer, this.camera);
    return this.raycaster.intersectObjects(this.scene.clickables);
  }

  onTick({ et }) {
    this.app.uniforms.uTime.value = et;
  }

  onRender() {
    this.t++;
    // this.renderer.setRenderTarget(null);
    // this.renderer.render(this.scene, this.camera);

    this.renderDiffuse();
    if (this.kong) this.renderEmissive();
    this.renderPostProcessing();
  }

  renderDiffuse() {
    this.renderRenderTarget(this.scene, this.camera, this.postProcessing.diffuseRenderTarget);
  }

  renderEmissive() {
    this.kong.onlyEmissive = true;
    this.renderRenderTarget(this.kong, this.camera, this.postProcessing.emissiveRenderTarget1);
    this.kong.onlyEmissive = false;

    this.postProcessing.emissiveUniforms.tEmissive.value =
      this.postProcessing.emissiveRenderTarget1.texture;
    this.postProcessing.emissiveUniforms.uHorizontal.value = 0;
    this.renderRenderTarget(
      this.postProcessing.emissiveQuad,
      this.postProcessing.camera,
      this.postProcessing.emissiveRenderTarget2
    );

    this.postProcessing.emissiveUniforms.tEmissive.value =
      this.postProcessing.emissiveRenderTarget2.texture;
    this.postProcessing.emissiveUniforms.uHorizontal.value = 1;
    this.renderRenderTarget(
      this.postProcessing.emissiveQuad,
      this.postProcessing.camera,
      this.postProcessing.emissiveRenderTarget3
    );
    // this.renderRenderTarget(this.postProcessing.emissiveQuad, this.postProcessing.camera, null);
  }

  renderPostProcessing() {
    this.renderer.setRenderTarget(null);
    this.renderer.clear();
    this.renderer.render(this.postProcessing.quad, this.postProcessing.camera);
  }

  renderRenderTarget(scene, camera, renderTarget) {
    this.renderer.setRenderTarget(renderTarget);
    this.renderer.clear();
    this.renderer.render(scene, camera);
  }
}
